import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/procurement`;
const PAGE_SIZE = 50;

interface PRLine { id: number; line_number: number; item_code: string; description: string; quantity: number; estimated_unit_price: number; estimated_amount: number; }
interface PR { id: number; pr_number: string; requester_name: string; department: string; total_estimated_cost: number; lifecycle_status: string; budget_check_passed: boolean | null; version: number; lines: PRLine[]; }
interface Summary { total: number; draft: number; submitted: number; approved: number; converted: number; total_estimated_cost: number; }

export default function Procurement() {
  const { entity } = useGlobalEntity();
  const [prs, setPRs] = useState<PR[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ pr_number: '', requester_name: '', department: '', justification: '', lines: [{ item_code: '', description: '', quantity: '', estimated_unit_price: '' }] });
  const [tab, setTab] = useState<'prs' | 'catalog' | 'analytics'>('prs');
  const [catalog, setCatalog] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setPRs(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = { ...form, lines: form.lines.filter(l => l.description).map(l => ({ ...l, quantity: parseFloat(l.quantity) || 0, estimated_unit_price: parseFloat(l.estimated_unit_price) || 0 })) };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('PR created', true); setShowCreate(false); setForm({ pr_number: '', requester_name: '', department: '', justification: '', lines: [{ item_code: '', description: '', quantity: '', estimated_unit_price: '' }] }); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAction = async (id: number, action: string, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
      if (res.ok) { showToast(`PR ${action}`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Action failed', false); }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { item_code: '', description: '', quantity: '', estimated_unit_price: '' }] });
  const updateLine = (i: number, field: string, value: string) => { const lines = [...form.lines]; (lines[i] as any)[field] = value; setForm({ ...form, lines }); };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const catalogColumns = [
    { key: 'item_code', label: 'Item Code', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'item_name', label: 'Item Name', render: (v: any) => v || '-' },
    { key: 'vendor_name', label: 'Vendor', render: (v: any, row: any) => v || row.vendor_id || '-' },
    { key: 'unit_price', label: 'Unit Price', align: 'right' as const, render: (v: any) => `$${v?.toFixed(2)}` },
    { key: 'is_active', label: 'Active', render: (v: any) => <span style={{ color: v ? 'var(--positive)' : 'var(--negative)', fontSize: 10, fontWeight: 600 }}>{v ? 'YES' : 'NO'}</span> },
  ];

  const prColumns = [
    { key: 'pr_number', label: 'PR #', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'requester_name', label: 'Requester', render: (v: any) => v || '-' },
    { key: 'department', label: 'Department', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v || '-'}</span> },
    { key: 'total_estimated_cost', label: 'Est. Cost', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'budget_check_passed', label: 'Budget', render: (v: any) => {
      if (v === true) return <span style={{ color: 'var(--positive)', fontSize: 10, fontWeight: 600 }}>PASS</span>;
      if (v === false) return <span style={{ color: 'var(--negative)', fontSize: 10, fontWeight: 600 }}>FAIL</span>;
      return <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>-</span>;
    }},
    { key: 'lifecycle_status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'actions', label: 'Actions', render: (_: any, pr: PR) => (
      <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
        {pr.lifecycle_status === 'DRAFT' && <ActionButton label="Submit" onClick={(e) => { e.stopPropagation(); handleAction(pr.id, 'submit', pr.version); }} variant="secondary" size="sm" />}
        {pr.lifecycle_status === 'SUBMITTED' && <ActionButton label="Approve" onClick={(e) => { e.stopPropagation(); handleAction(pr.id, 'approve', pr.version); }} variant="success" size="sm" />}
        {pr.lifecycle_status === 'APPROVED' && <ActionButton label="Convert to PO" onClick={(e) => { e.stopPropagation(); handleAction(pr.id, 'convert', pr.version); }} variant="primary" size="sm" />}
        {['DRAFT', 'APPROVED'].includes(pr.lifecycle_status) && <ActionButton label="Cancel" onClick={(e) => { e.stopPropagation(); handleAction(pr.id, 'cancel', pr.version); }} variant="danger" size="sm" />}
      </div>
    )},
  ];

  const lineColumns = [
    { key: 'line_number', label: '#' },
    { key: 'item_code', label: 'Item', render: (v: any) => v || '-' },
    { key: 'description', label: 'Description' },
    { key: 'quantity', label: 'Qty', align: 'right' as const },
    { key: 'estimated_unit_price', label: 'Unit Price', align: 'right' as const, render: (v: any) => `$${v?.toFixed(2)}` },
    { key: 'estimated_amount', label: 'Amount', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600 }}>${v?.toFixed(2)}</span> },
  ];

  return (
    <PageLayout
      title="Procurement"
      subtitle="Purchase requisitions, budget checks, and vendor catalog"
      action={{ label: '+ New PR', onClick: () => setShowCreate(true) }}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Total PRs', value: summary.total },
          { label: 'Draft', value: summary.draft, color: 'var(--text-secondary)' },
          { label: 'Submitted', value: summary.submitted, color: 'var(--warning)' },
          { label: 'Approved', value: summary.approved, color: 'var(--positive)' },
          { label: 'Converted', value: summary.converted, color: 'var(--accent)' },
          { label: 'Total Cost', value: formatAmountCompact(summary.total_estimated_cost), color: 'var(--accent)' },
        ]} />
      )}

      <TabBar
        tabs={[
          { key: 'prs', label: 'Requisitions' },
          { key: 'catalog', label: 'Vendor Catalog' },
          { key: 'analytics', label: 'Spend Analytics' },
        ]}
        active={tab}
        onChange={async (t) => { setTab(t as any); if (t === 'catalog') { const r = await authFetch(`${API}/catalog?limit=100`); if (r.ok) { const d = await r.json(); setCatalog(d.items || []); }} if (t === 'analytics') { const r = await authFetch(`${API}/spend-analytics`); if (r.ok) setAnalytics(await r.json()); }}}
      />

      {tab === 'prs' && (
        <FilterBar filters={[
          { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: (v) => { setStatusFilter(v); setPage(0); }, options: ['DRAFT', 'SUBMITTED', 'APPROVED', 'CONVERTED', 'REJECTED', 'CANCELLED'].map(s => ({ value: s, label: s })), width: 200 },
        ]} />
      )}

      {tab === 'catalog' ? (
        <DataTable
          columns={catalogColumns}
          rows={catalog}
          emptyMessage="No catalog items."
        />
      ) : tab === 'analytics' ? (analytics ? (
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Department</div>{analytics.by_department?.length === 0 ? <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No data</div> : analytics.by_department?.map((d: any, i: number) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 12 }}><span>{d.department}</span><span style={{ fontWeight: 600 }}>{formatAmount(d.total_spend)}</span></div>)}</div>
          <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Vendor</div>{analytics.by_vendor?.length === 0 ? <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No data</div> : analytics.by_vendor?.map((v: any, i: number) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 12 }}><span>{v.vendor_id}</span><span style={{ fontWeight: 600 }}>{formatAmount(v.total_spend)}</span></div>)}</div>
        </div>
      ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</div>) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
            <thead>
              <tr>
                {prColumns.map(col => (
                  <th key={col.key} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', textAlign: col.align || 'left' }}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={prColumns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>Loading...</td></tr>
              ) : prs.length === 0 ? (
                <tr><td colSpan={prColumns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No purchase requisitions found.</td></tr>
              ) : prs.map(pr => (
                <>
                  <tr key={pr.id} onClick={() => setExpandedId(expandedId === pr.id ? null : pr.id)} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', background: expandedId === pr.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                    {prColumns.map(col => (
                      <td key={col.key} style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)', textAlign: col.align || 'left' }}>
                        {col.render ? col.render((pr as any)[col.key], pr) : (pr as any)[col.key] ?? '-'}
                      </td>
                    ))}
                  </tr>
                  {expandedId === pr.id && pr.lines && (
                    <tr key={`${pr.id}-lines`}><td colSpan={prColumns.length} style={{ padding: '0 14px 12px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      <div style={{ marginTop: 8 }}>
                        <DataTable
                          columns={lineColumns}
                          rows={pr.lines}
                          rowKey="id"
                          emptyMessage="No line items."
                        />
                      </div>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
              <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: '28px' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
              <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
            </div>
          )}
        </>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Purchase Requisition" size="lg" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create PR" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="PR Number *" value={form.pr_number} onChange={e => setForm({ ...form, pr_number: e.target.value })} style={inputStyle} />
            <input placeholder="Requester Name" value={form.requester_name} onChange={e => setForm({ ...form, requester_name: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} style={inputStyle} />
          </div>
          <input placeholder="Justification" value={form.justification} onChange={e => setForm({ ...form, justification: e.target.value })} style={inputStyle} />
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text-secondary)' }}>LINE ITEMS</div>
            {form.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input placeholder="Item Code" value={l.item_code} onChange={e => updateLine(i, 'item_code', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="Description" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ ...inputStyle, flex: 3 }} />
                <input placeholder="Qty" type="number" value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="Unit Price" type="number" value={l.estimated_unit_price} onChange={e => updateLine(i, 'estimated_unit_price', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              </div>
            ))}
            <button onClick={addLine} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Add Line</button>
          </div>
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
